'use client';
import * as React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import dynamic from 'next/dynamic';
import SocialLinks from '@repo/ui/src/components/SocialLinks';
import LanguageSelector from '@components/LanguageSelector';
import FooterColumn, { ColumnProps } from '@repo/ui/src/components/FooterColumn';
import MarketingCaptureForm from '@repo/ui/src/components/MarketingCapture/_MarketingCaptureForm';
import { classNames } from '@repo/ui/src/utils/classNames';
import Container from '@repo/ui/src/primitives/Container';
import { MarketingSignupProps } from '@typings/marketingSignup';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Image from 'next/image';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

export interface FooterProps {
  emailSignup?: MarketingSignupProps;
  smsSignup?: MarketingSignupProps;
  bottomRowTitle: string;
  submit: string;
  disclaimer?: string;
  columns: Array<ColumnProps>;
}

const Footer = ({
  columns,
  bottomRowTitle,
  emailSignup,
  smsSignup,
  disclaimer,
  submit = 'Sign Me Up',
}: FooterProps) => {
  const { t } = useTranslation();
  const doNotSellText = t('cookieConsentManager.doNotSellText', undefined, {
    default: 'none',
  });

  return (
    <footer className="bg-neutral-900 relative">
      <Container maxWidth="small-body" className="flex justify-between py-12 flex-col sm:flex-row">
        <FooterColumn columns={columns} />
        <div>
          {(emailSignup || smsSignup) && (
            <div className="pb-6 flex flex-col gap-5 max-w-[370px]">
              <MarketingCaptureForm
                emailSignup={emailSignup}
                smsSignup={smsSignup}
                darkMode
                success={emailSignup?.success}
                failure={emailSignup?.failure}
                submitButton={submit}
                sourceDetail="footer"
                fullWidth
              />
              {disclaimer && (
                <Markdown
                  className={classNames(
                    'text-neutral-200 tra-text-web-body-s hover:[&>a]:text-brand-200',
                  )}
                >
                  {disclaimer}
                </Markdown>
              )}
            </div>
          )}

          <SocialLinks
            twitter="TraegerGrills"
            facebook="TraegerPelletGrills"
            instagram="traegergrills"
            pinterest="traegergrills"
            youtubeUser="TraegerGrills"
            darkMode
            width="30px"
          />
        </div>
      </Container>

      <div className="bg-neutral-800 text-white">
        <div className="flex gap-y-6 flex-col max-w-smallBody px-7 py-6 mx-auto justify-between items-start sm:flex-row sm:items-center">
          <div className="border-b border-warm-400 self-stretch pb-3 sm:pb-0 sm:border-0 flex">
            <LanguageSelector showLabel />
          </div>

          <div className="tra-text-web-body-s text-white">
            <span>©&nbsp;{bottomRowTitle}</span>
          </div>

          <div className="flex flex-col">
            <button
              onClick={() => window.DG_BANNER_API?.showConsentBanner()}
              className={classNames(
                'tra-text-web-body-s text-neutral-200 block py-1 text-left outline-offset-2',
                'focus:outline-brand-200 focus:outline-2 focus-visible:outline',
                'hover:underline hover:text-brand-200',
              )}
            >
              {t('cookieConsentManager.buttonText', undefined, {
                default: 'Cookie Policy and Preferences',
              })}
            </button>

            {doNotSellText !== 'none' && (
              <Anchor
                className={classNames(
                  'tra-text-web-body-s text-neutral-200 flex items-center gap-2 py-1 outline-offset-2',
                  'focus:outline-brand-200 focus:outline-2 focus-visible:outline',
                  'hover:underline hover:text-brand-200',
                )}
                externalLink="https://preferences.traeger.com/"
                isExternal
              >
                <Image
                  src="/images/privacy.png"
                  alt="Privacy"
                  width={30}
                  height={18}
                  className="inline-block"
                />
                {doNotSellText}
              </Anchor>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
